import { Component, OnInit, ViewChild, inject, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Table, TableModule } from 'primeng/table';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { DialogModule } from 'primeng/dialog';
import { ToastModule } from 'primeng/toast';
import { FormsModule, ReactiveFormsModule, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Select } from 'primeng/select';
import { MessageService } from 'primeng/api';
import { PaymentDefinitionsService } from '@services/payment-definitions.service';
import { ITenantUserDefinition, ITypeListItem } from '@shared/interfaces/payment-definitions.interface';
import { ColumnFilterComponent } from '@app/shared/components/column-filter/column-filter.component';
import { IconField } from 'primeng/iconfield';
import { InputIcon } from 'primeng/inputicon';
import { PageHeaderComponent } from '@app/shared/components/page-header/page-header.component';

@Component({
    selector: 'app-tenant-user-definition',
    imports: [
        CommonModule,
        TableModule,
        ButtonModule,
        InputTextModule,
        DialogModule,
        ToastModule,
        FormsModule,
        ReactiveFormsModule,
        Select,
        ColumnFilterComponent,
        IconField,
        InputIcon,
        PageHeaderComponent
    ],
    templateUrl: './tenant-user-definition.component.html',
    styleUrl: './tenant-user-definition.component.scss'
})
export class TenantUserDefinitionComponent implements OnInit {
    @ViewChild('dt') dt!: Table;

    tenantUsers: ITenantUserDefinition[] = [];
    tenantTypes: ITypeListItem[] = [];
    loading = true;
    dialogVisible = false;
    isEditMode = false;
    selectedUser: ITenantUserDefinition | null = null;
    submitted = false;
    userForm!: FormGroup;

    private paymentService = inject(PaymentDefinitionsService);
    private fb = inject(FormBuilder);
    private messageService = inject(MessageService);
    private cd = inject(ChangeDetectorRef);

    ngOnInit(): void {
        this.initializeForm();
        this.loadTenantUsers();
        this.loadTenantTypes();
    }

    onGlobalSearch(event: Event): void {
        const value = (event.target as HTMLInputElement).value;
        this.dt.filterGlobal(value, 'contains');
    }

    loadTenantUsers(): void {
        this.loading = true;
        this.cd.detectChanges();
        this.paymentService.getTenantUsers().subscribe({
            next: (res) => {
                this.tenantUsers = res.tenantUserDefinitions || [];
                this.loading = false;
                this.cd.detectChanges();
            },
            error: () => {
                this.loading = false;
                this.cd.detectChanges();
            }
        });
    }

    loadTenantTypes(): void {
        this.paymentService.getTenantTypeInformation().subscribe({
            next: (res) => {
                this.tenantTypes = (res.typeList || []).filter((t) => t.isActive);
            }
        });
    }

    initializeForm(): void {
        this.userForm = this.fb.group({
            userName: ['', [Validators.required, Validators.maxLength(255)]],
            password: ['', [Validators.required]],
            tenantId: [null, [Validators.required]]
        });
    }

    isInvalid(field: string): boolean {
        const control = this.userForm.get(field);
        return !!(control && control.invalid && (this.submitted || control.dirty || control.touched));
    }

    openCreate(): void {
        this.isEditMode = false;
        this.selectedUser = null;
        this.submitted = false;
        this.userForm.reset({ userName: '', password: '', tenantId: null });
        this.dialogVisible = true;
    }

    openEdit(user: ITenantUserDefinition): void {
        this.isEditMode = true;
        this.selectedUser = user;
        this.submitted = false;
        this.userForm.patchValue({
            userName: user.userName,
            password: '',
            tenantId: user.tenantDefinitionId
        });
        this.dialogVisible = true;
    }

    cancelDialog(): void {
        this.dialogVisible = false;
    }

    saveUser(): void {
        this.submitted = true;
        if (this.userForm.invalid) {
            return;
        }

        const formData = this.userForm.value;
        const request = {
            userName: formData.userName,
            password: formData.password,
            tenantId: formData.tenantId
        };

        if (!this.isEditMode) {
            this.paymentService.createTenantUser(request).subscribe({
                next: () => {
                    this.messageService.add({
                        severity: 'success',
                        summary: 'Success',
                        detail: 'Tenant user has been created successfully.'
                    });
                    this.loadTenantUsers();
                    this.dialogVisible = false;
                }
            });
        } else if (this.selectedUser) {
            this.paymentService.updateTenantUser(this.selectedUser.id, request).subscribe({
                next: () => {
                    this.messageService.add({
                        severity: 'success',
                        summary: 'Success',
                        detail: 'Tenant user has been updated successfully.'
                    });
                    this.loadTenantUsers();
                    this.dialogVisible = false;
                }
            });
        }
    }
}
